import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, Link, Navigate } from "react-router-dom";
import useApi from "../../hooks/useApi";
import Select from "react-select";
import DatePicker from "react-datepicker";
import moment from "moment";
import Swal from "sweetalert2";
import { activeStatus } from "../../widgets/PolicyItemStatus";
import useReload from "../../hooks/useReload";
import useApp from "../../hooks/useApp";
import ClientPolicyRequirement from "../policies/ClientPolicyRequirement";
import ClientPolicyAttachment from "../policies/ClientPolicyAttachment";
import PolicyItemStatus from "../../widgets/PolicyItemStatus";
import {
  Accordion,
  Button,
  Modal,
  Container,
  Form,
  InputGroup,
  Row,
  Col,
} from "react-bootstrap";
import { NavigateBackButton } from "../../widgets";

const ClientAccountPolicyView = () => {
  const navigate = useNavigate();
  const { itemid = null } = useParams();
  const titleRef = useRef();
  const branchRef = useRef();
  const policyNumberRef = useRef();
  const premiumRef = useRef();

  const [policyCurrency, setPolicyCurrency] = useState("GHS");

  const [policy, setPolicy] = useState({});
  const [underwritePolicy, setUnderwritePolicy] = useState({});
  const [dropDowns, setDropDowns] = useState({ branch: [] });
  const { api, abortSignal, auth } = useApi();
  const { showAlert } = useApp();
  const { toggleReload, reload } = useReload();
  const [startDate, setStartDate] = useState(new Date());

  const getDropDowns = async (models) => {
    try {
      const { data } = await api.list(`/dropdowns`, models);
      return data;
    } catch (error) {
      console.error(error);
    }
  };

  const companyDropDown = async () => {
    try {
      const data = await getDropDowns(["company"]);
      if (data && typeof policy.CompanyId === "string")
        setPolicy({
          ...policy,
          CompanyId: data.company.find(
            (item) => item.value == policy.CompanyId
          ),
        });
      setDropDowns({ ...dropDowns, ...data });
    } catch (error) {}
  };

  const branchDropDown = async () => {
    try {
      const data = await getDropDowns([
        {
          branch: { CompanyId: policy.CompanyId.value },
        },
      ]);
      if (data && typeof policy.BranchId === "string")
        setPolicy({
          ...policy,
          BranchId: data.branch.find((item) => item.value == policy.BranchId),
        });
      setDropDowns({ ...dropDowns, ...data });
    } catch (error) {}
  };

  const getPolicy = async () => {
    try {
      if (itemid) {
        const { data = {} } = await api.read(
          `/policies/policy-items/${itemid}`
        );

        setPolicy({
          ...data,
          effectiveDate: data?.effectiveDate
            ? new Date(data?.effectiveDate)
            : new Date(),
          policyCurrency: {
            label: data.policyCurrency,
            value: data.policyCurrency,
          },
        });
      }
    } catch (error) {}
  };

  useEffect(() => {
    getPolicy();
  }, [auth, itemid, reload]);

  useEffect(() => {
    companyDropDown();
  }, [policy]);

  useEffect(() => {
    branchDropDown();
  }, [policy.CompanyId]);

  useEffect(() => {
    const currency = policy.policyCurrency?.value || "";
    setPolicyCurrency(currency);
  }, [policy.policyCurrency]);

  useEffect(() => {
    setPolicy({
      ...policy,
      expires: moment(policy?.effectiveDate || new Date())
        .add(policy?.duration || 12, "months")
        .subtract(1, "day")
        .format("YYYY-MM-DD"),
    });
  }, [policy?.duration, policy?.effectiveDate]);

  return (
    <>
      <section id="content" className="content">
        <div className="content__header content__boxed">
          <div className="content__wrap">
            {/* <!-- Breadcrumb --> */}
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to="/dashboard">Home</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/client-policies">My Policies</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Detail
                </li>
              </ol>
            </nav>
            {/* <!-- END : Breadcrumb --> */}

            <h1 className="page-title mb-0 mt-2">Policies View</h1>

            <p className="lead">View policy detail</p>

            {policy && (
              <div className="d-md-flex align-items-baseline mt-3">
                <h2 className="me-2">
                  <PolicyItemStatus status={policy?.status} />
                </h2>
                {["active", "expiring"].includes(policy?.status) && (
                  <>
                    <Button
                      variant="warning me-2"
                      size="xs"
                      onClick={() =>
                        navigate(
                          `/client-account/claims/add/${policy?.ClientPolicyId}`
                        )
                      }
                    >
                      <i className="bi bi-exclamation-circle fs-5 me-2" />
                      Log Claim
                    </Button>
                  </>
                )}
                {!["pending"].includes(policy?.status) && (
                  <Button
                    variant="info me-2"
                    size="xs"
                    onClick={() =>
                      navigate(
                        `/client-account/claims?client-policy=${policy?.ClientPolicyId}`
                      )
                    }
                  >
                    <i className="bi bi-exclamation fs-5 me-2" />
                    Claims
                  </Button>
                )}
                <div className="d-flex align-items-center gap-1 text-nowrap ms-auto mb-3">
                  <NavigateBackButton />
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="content__boxed">
          <div className="content__wrap">
            <Accordion defaultActiveKey="0" className="mb-2">
              <Accordion.Item eventKey="0" className="mb-2">
                <Accordion.Header>Attachments</Accordion.Header>
                <Accordion.Body>
                  <ClientPolicyAttachment
                    readOnly={true}
                    policy={policy}
                    hideAction={true}
                  />
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>

            <Accordion defaultActiveKey="0" className="mb-2">
              <Accordion.Item eventKey="0">
                <Accordion.Header>Policy Requirements</Accordion.Header>
                <Accordion.Body>
                  <ClientPolicyRequirement
                    readOnly={true}
                    policy={policy}
                    hideSave={true}
                  />
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
            <Form
              // onSubmit={handleUnderwrite}
              autoComplete="off"
              id="underwriteForm"
              className="mb-5"
            >
              {policy && (
                <div className="card h-100">
                  <fieldset
                    disabled={true}
                    // disabled={
                    //   activeStatus.includes(policy?.status) ? true : false
                    // }
                  >
                    <div className="card-body">
                      <Row>
                        <Container fluid="sm">
                          <Row className="mb-5">
                            <Form.Group>
                              <Form.Label className="text-primary">
                                Title
                              </Form.Label>
                              <Form.Control
                                id="title"
                                type="text"
                                ref={titleRef}
                                onChange={(e) =>
                                  setPolicy({
                                    ...policy,
                                    title: e.target.value,
                                  })
                                }
                                value={policy?.title || ""}
                              />
                              <Form.Text>
                                This is label for the policy
                              </Form.Text>
                            </Form.Group>
                          </Row>
                          <Row className="mb-4">
                            <Col sm={12} md={6}>
                              <Form.Group>
                                <Form.Label className="text-primary">
                                  Product
                                </Form.Label>
                                <Form.Control
                                  plaintext
                                  readOnly
                                  value={`${policy?.PolicyType?.name} - ${policy?.Product?.name}`}
                                  onChange={() => {}}
                                />
                              </Form.Group>
                            </Col>
                            <Col sm={12} md={6}>
                              <Form.Group>
                                <Form.Label className="text-primary">
                                  Insuring Company
                                </Form.Label>
                                <Select
                                  id="company"
                                  options={
                                    dropDowns && dropDowns["company"]
                                      ? dropDowns["company"]
                                      : []
                                  }
                                  onChange={(selectedOption) =>
                                    setPolicy({
                                      ...policy,
                                      CompanyId: selectedOption,
                                      BranchId: "",
                                    })
                                  }
                                  value={policy?.CompanyId || ""}
                                />
                              </Form.Group>
                            </Col>
                          </Row>
                          <Row className="mb-4">
                            <Col sm={12} md={6}>
                              <Form.Group>
                                <Form.Label className="text-primary">
                                  Underwriting Branch
                                </Form.Label>
                                <Select
                                  ref={branchRef}
                                  id="branch"
                                  options={
                                    dropDowns && dropDowns["branch"]
                                      ? dropDowns["branch"]
                                      : []
                                  }
                                  onChange={(selectedOption) =>
                                    setPolicy({
                                      ...policy,
                                      BranchId: selectedOption,
                                    })
                                  }
                                  value={policy?.BranchId || ""}
                                />
                              </Form.Group>
                            </Col>
                            <Col sm={12} md={6}>
                              <Form.Group>
                                <Row>
                                  <Col>
                                    <Form.Label className="text-primary">
                                      Policy Number
                                    </Form.Label>
                                  </Col>
                                  {policy?.Policy?.policyNumber && (
                                    <Col>
                                      <div className="form-check form-switch">
                                        <Form.Label className="text-primary">
                                          Use Parent Policy Number
                                        </Form.Label>

                                        <Form.Control
                                          id="copy-policy-number"
                                          className="form-check-input me-3"
                                          type="checkbox"
                                          checked={
                                            policy?.policyNumber ==
                                            policy?.Policy?.policyNumber
                                              ? true
                                              : false
                                          }
                                          onChange={(e) => {
                                            if (e.target.checked) {
                                              setPolicy({
                                                ...policy,
                                                policyNumber:
                                                  policy?.Policy?.policyNumber,
                                              });
                                            }
                                          }}
                                        />
                                      </div>
                                    </Col>
                                  )}
                                </Row>

                                <Form.Control
                                  ref={policyNumberRef}
                                  id="policy-number"
                                  type="text"
                                  onChange={(e) =>
                                    setPolicy({
                                      ...policy,
                                      policyNumber: e.target.value,
                                    })
                                  }
                                  value={policy?.policyNumber || ""}
                                />
                              </Form.Group>
                            </Col>
                          </Row>
                          <Row className="mb-3">
                            <Col sm={12} md={6}>
                              <Form.Group>
                                <Form.Label className="text-primary">
                                  Policy Currency
                                </Form.Label>
                                <Select
                                  id="currency"
                                  options={[
                                    { label: "GHS", value: "GHS" },
                                    { label: "USD", value: "USD" },
                                  ]}
                                  onChange={(selectedOption) =>
                                    setPolicy({
                                      ...policy,
                                      policyCurrency: selectedOption,
                                    })
                                  }
                                  value={policy?.policyCurrency || "GHS"}
                                />
                              </Form.Group>
                            </Col>

                            <Col sm={12} md={6}>
                              <Form.Group>
                                <Form.Label className="text-primary">
                                  Premium
                                </Form.Label>
                                <InputGroup className="mb-3">
                                  <InputGroup.Text>
                                    {policyCurrency || "GHS"}
                                  </InputGroup.Text>
                                  <Form.Control
                                    aria-label="Amount (to the nearest value)"
                                    ref={premiumRef}
                                    id="premium"
                                    type="number"
                                    min={0}
                                    step={"0.01"}
                                    value={policy?.premium || 0.0}
                                    onChange={(e) =>
                                      setPolicy({
                                        ...policy,
                                        premium: e.target.value,
                                      })
                                    }
                                  />
                                  <InputGroup.Text className="fw-bold">
                                    {Number.parseFloat(
                                      policy?.premium
                                    ).toLocaleString("en-US")}
                                  </InputGroup.Text>
                                </InputGroup>
                              </Form.Group>
                            </Col>
                          </Row>
                          <Row className="mb-3">
                            <Col sm={12} md={6}>
                              <Form.Group>
                                <Form.Label className="text-primary">
                                  Effective Date
                                </Form.Label>
                                <DatePicker
                                  dateFormat="dd/MM/yyyy"
                                  showMonthDropdown
                                  showPopperArrow={false}
                                  id="effective-date"
                                  className="form-control"
                                  selected={policy?.effectiveDate || startDate}
                                  onChange={(date) =>
                                    setPolicy({
                                      ...policy,
                                      effectiveDate: date,
                                    })
                                  }
                                  isClearable
                                  placeholderText=""
                                />
                              </Form.Group>
                            </Col>
                            <Col sm={12} md={6}>
                              <Form.Group>
                                <Form.Label className="text-primary">
                                  Duration (in months)
                                </Form.Label>
                                <Form.Control
                                  id="duration"
                                  type="number"
                                  required
                                  placeholder="Duraton in months"
                                  onChange={(e) =>
                                    setPolicy({
                                      ...policy,
                                      duration: e.target.value,
                                    })
                                  }
                                  value={policy?.duration || 12}
                                  min={1}
                                />
                              </Form.Group>
                            </Col>
                          </Row>
                          <Row className="mb-3">
                            <Col sm={12} md={6}>
                              <Form.Group>
                                <Form.Label className="text-primary">
                                  Created
                                </Form.Label>
                                <Form.Control
                                  plaintext
                                  onChange={(e) => {}}
                                  value={
                                    moment(policy?.createdAt).format(
                                      "DD/MM/YYYY"
                                    ) || ""
                                  }
                                />
                              </Form.Group>
                            </Col>
                            <Col sm={12} md={6}>
                              <Form.Group>
                                <Form.Label className="text-primary">
                                  Expires
                                </Form.Label>
                                <Form.Control
                                  className="fs-4"
                                  plaintext
                                  id="expires"
                                  onChange={(e) => {}}
                                  value={
                                    moment(policy?.expires).format(
                                      "DD/MM/YYYY"
                                    ) || ""
                                  }
                                />
                              </Form.Group>
                            </Col>
                          </Row>
                          <Row className="mb-3">
                            <Col sm={12} md={6}>
                              {(["waiting", "renewal"].includes(
                                policy?.status
                              ) ||
                                policy.deferCommission == true) && (
                                <Form.Group>
                                  <Form.Label className="text-primary">
                                    Defer Commission
                                  </Form.Label>
                                  <div className="form-check form-switch">
                                    <Form.Control
                                      id="defer"
                                      className="form-check-input mt-2"
                                      type="checkbox"
                                      aria-describedby="deferCommissionHelp"
                                      checked={
                                        policy.deferCommission == true
                                          ? true
                                          : false
                                      }
                                      onChange={(e) =>
                                        setPolicy({
                                          ...policy,
                                          deferCommission: e.target.checked,
                                        })
                                      }
                                    />
                                    <br />
                                    <br />
                                    <Form.Text>
                                      Please note this requires approval from
                                      Management before policy is activated
                                    </Form.Text>
                                  </div>
                                </Form.Group>
                              )}
                            </Col>
                          </Row>
                          <Row className="mb-3">
                            <Form.Group>
                              <Form.Label className="text-primary">
                                Other info
                              </Form.Label>
                              <Form.Control
                                as="textarea"
                                placeholder="Info"
                                onChange={(e) =>
                                  setPolicy({
                                    ...policy,
                                    info: e.target.value,
                                  })
                                }
                                value={policy?.info || ""}
                                rows="6"
                              />
                              <Form.Text>Enter details of the Policy</Form.Text>
                            </Form.Group>
                          </Row>
                        </Container>
                      </Row>
                    </div>
                  </fieldset>
                </div>
              )}
            </Form>
          </div>
        </div>
      </section>
    </>
  );
};

export default ClientAccountPolicyView;
